// pages/LoginPage.tsx
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/auth.service';
import { Loader2, Phone, ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import '@/styles/login.css';

type Step = 'phone' | 'otp';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();

  const [step, setStep] = useState<Step>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [phoneFocused, setPhoneFocused] = useState(false);

  const from = (location.state as any)?.from?.pathname || '/';

  // Initialize reCAPTCHA once and cleanup on unmount
  useEffect(() => {
    authService.initializeRecaptcha('recaptcha-container');
    
    // Cleanup: reset verifier when component unmounts
    return () => {
      authService.resetRecaptcha();
    };
  }, []);

  const startResendTimer = () => {
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendOTP = async () => {
    try {
      setLoading(true);
      await authService.signInWithPhoneNumber(`+91${phoneNumber}`);
      startResendTimer();
      toast.success('Code sent again');
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneDisplay = (phone: string) => {
    if (phone.length === 10) return `+91 ${phone.substring(0, 5)} ${phone.substring(5)}`;
    return phone;
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phoneNumber.length !== 10 || !/^[6-9]\d{9}$/.test(phoneNumber)) {
      toast.error('Please enter a valid mobile number');
      return;
    }

    setLoading(true);
    try {
      await authService.signInWithPhoneNumber(`+91${phoneNumber}`);
      setStep('otp');
      startResendTimer();
      toast.success('Verification code sent');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    try {
      await authService.confirmVerificationCode(otp);
      await refreshUser();
      toast.success('Welcome back');
      navigate(from, { replace: true });
    } catch (error: any) {
      toast.error(error.message || 'Invalid code');
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  // const handleGoogleSignIn = async () => {
  //   setLoading(true);
  //   try {
  //     await authService.signInWithGoogle();
  //     await refreshUser();
  //     toast.success('Welcome');
  //     navigate(from, { replace: true });
  //   } catch (error: any) {
  //     toast.error(error.message || 'Failed to sign in');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  return (
    <>
      <Helmet>
        <title>Welcome | Sunrise Bookstore</title>
      </Helmet>

      {/* Main Container */}
      <div className="min-h-screen relative overflow-hidden">
        {/* Background with subtle bookstore ambiance */}
        <div 
          className="absolute inset-0 bg-[#F8F5F0]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23noise)' opacity='0.05'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Subtle backdrop */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#F8F5F0] via-[#F5F2ED] to-[#F8F5F0] opacity-80" />

        {/* Content Container */}
        <div className="relative min-h-screen flex items-center justify-center px-6 py-20 sm:px-8">
          <div className="w-full max-w-[400px]">
            
            {/* Elegant Header */}
            <div className="text-center mb-12">
              <h1 className="text-[24px] font-light text-[#2D2D2D] mb-2 tracking-wide" style={{ fontStyle: 'italic' }}>
                {step === 'phone' ? 'Welcome' : 'Verification'}
              </h1>
              <div className="w-12 h-px bg-[#2D2D2D] opacity-20 mx-auto mt-6" />
            </div>

            {/* Form Card */}
            <div className="bg-white/40 backdrop-blur-sm rounded-lg border border-[#2D2D2D]/5 p-10 sm:p-12">
              
              {step === 'phone' ? (
                // PHONE NUMBER STEP
                <>
                  <div className="mb-8">
                    <p className="text-[14px] text-[#2D2D2D]/60 text-center">
                      Enter your mobile number to continue
                    </p>
                  </div>

                  <form onSubmit={handleSendOTP} className="space-y-8">
                    {/* Phone Number Input */}
                    <div className="relative">
                      <label 
                        className={`
                          absolute left-0 transition-all duration-200 pointer-events-none
                          ${phoneFocused || phoneNumber ? 'text-[12px] -top-5 text-[#A8523A]' : 'text-[16px] top-3 text-[#2D2D2D]/40'}
                        `}
                      >
                        Mobile Number
                      </label>
                      <div className={` ${phoneFocused || phoneNumber ? 'relative' : 'opacity-0'} `}>
                        <div className="absolute left-0 top-3 flex items-center gap-2 pointer-events-none text-[#2D2D2D]/60">
                          <Phone className="w-[18px] h-[18px]" />
                          <span className="text-[16px]">+91</span>
                        </div>
                        <input
                          type="tel"
                          value={phoneNumber}
                          onChange={(e) =>
                            setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))
                          }
                          onFocus={() => setPhoneFocused(true)}
                          onBlur={() => setPhoneFocused(false)}
                          placeholder="9876543210"
                          disabled={loading}
                          className="
                            w-full bg-transparent border-0 border-b border-[#2D2D2D]/10
                            pt-3 pb-2 pl-16 pr-0
                            text-[16px] text-[#2D2D2D] placeholder-[#2D2D2D]/20
                            focus:outline-none focus:border-[#A8523A]
                            transition-all duration-150
                            disabled:opacity-50
                          "
                          style={{
                            borderBottomWidth: phoneFocused ? '2px' : '1px',
                          }}
                          maxLength={10}
                          required
                        />
                      </div>
                    </div>

                   

                    {/* Send Code Button */}
                    <button
                      type="submit"
                      disabled={loading || phoneNumber.length !== 10}
                      className="
                        w-full mt-8 py-4 px-6
                        bg-[#A8523A] text-white text-[16px] font-medium tracking-wide
                        rounded-md
                        transition-all duration-150
                        hover:bg-[#8F4430] hover:scale-[1.02]
                        active:scale-[0.98]
                        disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100
                        focus:outline-none focus:ring-2 focus:ring-[#A8523A]/20 focus:ring-offset-2
                      "
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Sending...</span>
                        </span>
                      ) : (
                        'Send Verification Code'
                      )}
                    </button>

                    {/* reCAPTCHA container - now visible */}
                    {/* <div className="mt-6 flex justify-center"> */}
                      <div id="recaptcha-container" />
                    {/* </div> */}
                  </form>

                  {/* OAuth Divider */}
                  {/* <div className="relative my-10">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full h-px bg-[#2D2D2D]/10" />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="px-4 bg-white/40 text-[14px] text-[#2D2D2D]/40">
                        or continue with
                      </span>
                    </div>
                  </div> */}

                  {/* Google Sign In */}
                  {/* <div className="flex items-center justify-center">
                    <button
                      onClick={handleGoogleSignIn}
                      disabled={loading}
                      className="
                        w-[44px] h-[44px] rounded-full
                        bg-white border border-[#2D2D2D]/10
                        flex items-center justify-center
                        transition-all duration-200
                        hover:border-[#A8523A]/30 hover:scale-110
                        active:scale-95
                        disabled:opacity-40 disabled:cursor-not-allowed
                      "
                      title="Continue with Google"
                    >
                      <svg className="w-[20px] h-[20px]" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </button>
                  </div> */}
                </>
              ) : (
                // OTP VERIFICATION STEP
                <>
                  {/* Back Button */}
                  <button
                    onClick={() => setStep('phone')}
                    disabled={loading}
                    className="
                      flex items-center gap-2 mb-8
                      text-[14px] text-[#A8523A] hover:text-[#8F4430]
                      transition-colors duration-200
                      disabled:opacity-50
                    "
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Change number</span>
                  </button>

                  <div className="mb-8 text-center">
                    <p className="text-[14px] text-[#2D2D2D]/60">
                      Enter the code sent to
                    </p>
                    <p className="text-[16px] text-[#2D2D2D] font-medium mt-1">
                      {formatPhoneDisplay(phoneNumber)}
                    </p>
                  </div>

                  <form onSubmit={handleVerifyOTP} className="space-y-8">
                    {/* OTP Input */}
                    <div className="relative">
                      <input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        disabled={loading}
                        autoFocus
                        className="
                          w-full bg-transparent border-0 border-b-2 border-[#A8523A]
                          py-4 px-0
                          text-[32px] text-[#2D2D2D] text-center tracking-[0.5em] font-light
                          placeholder-[#2D2D2D]/20
                          focus:outline-none focus:border-[#8F4430]
                          transition-all duration-150
                          disabled:opacity-50
                        "
                        maxLength={6}
                      />
                    </div>

                    {/* Resend Timer */}
                    <div className="text-center">
                      {resendTimer > 0 ? (
                        <p className="text-[14px] text-[#2D2D2D]/40">
                          Resend code in {resendTimer}s
                        </p>
                      ) : (
                        <button
                          type="button"
                          onClick={handleResendOTP}
                          disabled={loading}
                          className="
                            text-[14px] text-[#A8523A] hover:text-[#8F4430]
                            transition-colors duration-200
                            relative group
                            disabled:opacity-50
                          "
                        >
                          Resend verification code
                          <span className="absolute bottom-0 left-0 w-0 h-px bg-[#A8523A] transition-all duration-200 group-hover:w-full" />
                        </button>
                      )}
                    </div>

                    {/* Verify Button */}
                    <button
                      type="submit"
                      disabled={loading || otp.length !== 6}
                      className="
                        w-full mt-8 py-4 px-6
                        bg-[#A8523A] text-white text-[16px] font-medium tracking-wide
                        rounded-md
                        transition-all duration-150
                        hover:bg-[#8F4430] hover:scale-[1.02]
                        active:scale-[0.98]
                        disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100
                        focus:outline-none focus:ring-2 focus:ring-[#A8523A]/20 focus:ring-offset-2
                      "
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Verifying...</span>
                        </span>
                      ) : (
                        'Verify & Continue'
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>

            {/* Subtle Brand Mark */}
            <div className="text-center mt-12">
              <p className="text-[12px] text-[#2D2D2D]/30 tracking-wider">
                SUNRISE BOOKSTORE
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;